
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { useAuth } from '@/contexts/AuthContext';
import { useRazorpay } from '@/hooks/useRazorpay';
import { supabase } from '@/integrations/supabase/client';
import { 
  Crown, 
  Zap, 
  Star, 
  CheckCircle, 
  Users, 
  Sparkles,
  CreditCard,
  Shield,
  ArrowLeft,
  Gift,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import LoadingSpinner from '@/components/LoadingSpinner';
import { PRICING_PLANS } from '@/constants/pricing';

const Billing = () => {
  const { user } = useAuth();
  const { createSubscription, isProcessing } = useRazorpay();
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      fetchUserProfile();
    }
  }, [user]);

  const fetchUserProfile = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', user?.id)
        .single();

      if (error) {
        console.error('Error fetching user profile:', error);
        setError('Failed to load profile data');
        toast.error('Failed to load profile data');
      } else if (data) {
        setUserProfile(data);
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
      setError('Failed to load profile data');
      toast.error('Failed to load profile data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (plan: 'starter' | 'pro' | 'team') => {
    if (!user) {
      toast.error('Please log in to subscribe');
      navigate('/');
      return;
    }

    try {
      console.log('Initiating subscription for plan:', plan);
      await createSubscription(plan);
    } catch (error) {
      console.error('Subscription error:', error);
      toast.error('Failed to create subscription. Please try again.');
    }
  };

  const plans = [
    {
      ...PRICING_PLANS.free,
      icon: Gift,
      color: 'from-muted-foreground to-muted-foreground',
      current: user?.plan === 'free' || !user?.plan,
      popular: false
    },
    {
      ...PRICING_PLANS.pro,
      icon: Crown,
      color: 'from-purple-600 to-blue-600',
      popular: true,
      current: user?.plan === 'pro'
    },
    {
      ...PRICING_PLANS.business,
      icon: Users,
      color: 'from-emerald-600 to-teal-600',
      current: user?.plan === 'team' || user?.plan === 'business',
      popular: false
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <LoadingSpinner size="lg" text="Loading billing information..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="max-w-md mx-auto">
          <CardContent className="p-8 text-center">
            <AlertCircle className="w-12 h-12 text-destructive mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2 text-foreground">Error Loading Billing</h3>
            <p className="text-muted-foreground mb-4">{error}</p>
            <Button onClick={fetchUserProfile} variant="outline">
              Try Again
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8 md:py-12">
        {/* Go Back Button */}
        <div className="mb-6 md:mb-8">
          <Button
            onClick={() => navigate(-1)}
            variant="outline"
            className="flex items-center space-x-2"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Go Back</span>
          </Button>
        </div>

        {/* Hero Section */}
        <div className="text-center mb-12 md:mb-16">
          <div className="inline-flex items-center space-x-2 bg-card backdrop-blur-sm px-4 md:px-6 py-3 rounded-full border border-border mb-6 md:mb-8">
            <Sparkles className="w-4 md:w-5 h-4 md:h-5 text-primary" />
            <span className="font-semibold text-card-foreground text-sm md:text-base">Professional AI Content Generation</span>
          </div>
          
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold bg-gradient-to-r from-purple-600 via-blue-600 to-emerald-600 bg-clip-text text-transparent mb-4 md:mb-6">
            Billing & Plans
          </h1>
          
          <p className="text-lg md:text-xl text-muted-foreground mb-6 md:mb-8 max-w-3xl mx-auto px-4">
            Manage your subscription and unlock the full potential of AI content creation with Genova AI Studio.
          </p>

          {/* Current Usage Badge */}
          {user && (
            <div className="inline-flex flex-col md:flex-row items-center space-y-2 md:space-y-0 md:space-x-4 bg-card backdrop-blur-sm px-6 md:px-8 py-4 rounded-2xl border border-border shadow-lg">
              <div className="flex items-center space-x-2">
                <Star className="w-4 md:w-5 h-4 md:h-5 text-yellow-500" />
                <span className="font-semibold text-sm md:text-base text-foreground">Current Plan:</span>
                <Badge variant="outline" className="capitalize font-bold text-xs md:text-sm">
                  {user.plan || 'Free'}
                </Badge>
              </div>
              <Separator orientation="vertical" className="hidden md:block h-6" />
              <div className="flex items-center space-x-2">
                <Zap className="w-4 md:w-5 h-4 md:h-5 text-blue-500" />
                <span className="font-semibold text-sm md:text-base text-foreground">
                  {user.creditsUsed || 0} / {user.creditsLimit || 5} generations used
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-3 gap-6 md:gap-8 max-w-7xl mx-auto mb-12 md:mb-16">
          {plans.map((plan) => {
            const IconComponent = plan.icon;
            
            return (
              <Card 
                key={plan.id}
                className={`relative overflow-hidden transition-all duration-300 hover:scale-105 ${
                  plan.popular 
                    ? 'ring-2 ring-purple-500 shadow-2xl' 
                    : 'hover:shadow-xl'
                } ${plan.current ? 'ring-2 ring-emerald-500' : ''}`}
              >
                {plan.popular && (
                  <div className="absolute top-0 left-0 right-0 bg-gradient-to-r from-purple-600 to-blue-600 text-white text-center py-2 font-semibold text-sm">
                    ⭐ MOST POPULAR
                  </div>
                )}
                
                {plan.current && (
                  <div className="absolute top-0 left-0 right-0 bg-gradient-to-r from-emerald-600 to-teal-600 text-white text-center py-2 font-semibold text-sm">
                    ✅ CURRENT PLAN
                  </div>
                )}

                <CardHeader className={`text-center pb-6 ${plan.popular || plan.current ? 'pt-12' : 'pt-8'}`}>
                  <div className={`w-14 md:w-16 h-14 md:h-16 mx-auto rounded-2xl bg-gradient-to-r ${plan.color} flex items-center justify-center mb-4 md:mb-6`}>
                    <IconComponent className="w-6 md:w-8 h-6 md:h-8 text-white" />
                  </div>
                  
                  <CardTitle className="text-xl md:text-2xl font-bold mb-2 text-card-foreground">{plan.name}</CardTitle>
                  <p className="text-muted-foreground mb-4 md:mb-6 text-sm md:text-base">{plan.description}</p>
                  
                  <div className="flex items-baseline justify-center">
                    <span className="text-3xl md:text-4xl font-bold text-foreground">{plan.price}</span>
                    <span className="text-muted-foreground ml-2 text-sm md:text-base">{plan.period}</span>
                  </div>
                </CardHeader>

                <CardContent className="px-6 md:px-8 pb-6 md:pb-8">
                  <ul className="space-y-3 md:space-y-4 mb-6 md:mb-8">
                    {plan.features.map((feature, index) => (
                      <li key={index} className="flex items-start space-x-3">
                        <CheckCircle className="w-4 md:w-5 h-4 md:h-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                        <span className="text-muted-foreground text-sm md:text-base">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {plan.id === 'free' ? (
                    <Button 
                      variant="outline" 
                      className="w-full h-10 md:h-12 font-semibold text-sm md:text-base"
                      disabled
                    >
                      Current Plan
                    </Button>
                  ) : (
                    <Button
                      onClick={() => handleSubscribe(plan.id as 'pro' | 'team')}
                      disabled={isProcessing || plan.current}
                      className={`w-full h-10 md:h-12 font-semibold transition-all duration-200 text-sm md:text-base ${
                        plan.popular
                          ? 'bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700'
                          : 'bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700'
                      } text-white shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      {isProcessing ? (
                        <div className="flex items-center space-x-2">
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          <span>Processing...</span>
                        </div>
                      ) : plan.current ? (
                        'Current Plan'
                      ) : (
                        <>
                          <CreditCard className="w-4 md:w-5 h-4 md:h-5 mr-2" />
                          Subscribe Now
                        </>
                      )}
                    </Button>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* User Profile Status */}
        {userProfile && (
          <div className="mt-8 md:mt-12 max-w-2xl mx-auto">
            <Card className="border border-border">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <CheckCircle className="w-5 md:w-6 h-5 md:h-6 text-emerald-600" />
                  <span className="text-lg md:text-xl text-card-foreground">Account Status</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Plan</p>
                    <p className="font-semibold capitalize text-base md:text-lg text-foreground">{userProfile.plan || 'Free'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Credits Used</p>
                    <p className="font-semibold text-base md:text-lg text-foreground">
                      {userProfile.credits_used || 0} / {userProfile.credits_limit || 5}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default Billing;
